import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getCartItems, removeItemFromCart, calculateCartTotals, buildCheckoutPayload } from '../utils/cartUtils';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { LoadingSpinner } from '../components/LoadingSpinner';
import { toast } from 'sonner';
import { ArrowLeft, MapPin, ShoppingCart, X } from 'lucide-react';

function CheckoutItem(props) {
  return (
    <div className="stat-card p-4 flex items-center gap-4">
      <div className="w-16 h-20 bg-zinc-800 rounded overflow-hidden flex-shrink-0">
        {props.image ? (
          <img src={props.image} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-zinc-600 text-xs">No Img</div>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-white font-medium truncate">{props.name}</p>
        <p className="text-sm text-zinc-500">{props.year} {props.set}</p>
        <p className="text-sm text-zinc-500">{props.grade}</p>
        <p className="font-mono text-emerald-400 mt-1">${props.price}</p>
      </div>
      <button onClick={props.onRemove} className="p-2 text-zinc-500 hover:text-red-400">
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}

function ShippingLine(props) {
  return (
    <div className="flex justify-between text-sm">
      <span className="text-zinc-400">Shipping ({props.seller})</span>
      <span className="text-white">${props.amount}</span>
    </div>
  );
}

export function CheckoutPage() {
  var navigate = useNavigate();
  var auth = useAuth();
  var api = auth.api;
  var isAuthenticated = auth.isAuthenticated;
  var user = auth.user;
  
  var [items, setItems] = useState([]);
  var [loading, setLoading] = useState(false);
  var [shippingAddress, setShippingAddress] = useState({
    name: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    country: 'USA'
  });

  useEffect(function() {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    
    var storedIds = sessionStorage.getItem('checkout_items');
    if (!storedIds) {
      toast.error('No items to checkout');
      navigate('/marketplace');
      return;
    }
    
    var itemIds = JSON.parse(storedIds);
    var cartItems = getCartItems();
    var checkoutItems = [];
    
    for (var i = 0; i < cartItems.length; i++) {
      for (var j = 0; j < itemIds.length; j++) {
        if (cartItems[i].id === itemIds[j]) {
          checkoutItems.push(cartItems[i]);
          break;
        }
      }
    }
    
    if (checkoutItems.length === 0) {
      toast.error('Items not found in cart');
      navigate('/marketplace');
      return;
    }
    
    setItems(checkoutItems);
    
    if (user && user.name) {
      setShippingAddress(function(prev) {
        return { ...prev, name: user.name };
      });
    }
  }, [isAuthenticated, navigate, user]);

  var totals = calculateCartTotals(items);

  function handleRemoveItem(itemId) {
    removeItemFromCart(itemId);
    var newItems = [];
    for (var i = 0; i < items.length; i++) {
      if (items[i].id !== itemId) {
        newItems.push(items[i]);
      }
    }
    setItems(newItems);
    window.dispatchEvent(new Event('cartUpdated'));
    
    if (newItems.length === 0) {
      sessionStorage.removeItem('checkout_items');
      navigate('/marketplace');
    } else {
      var newIds = [];
      for (var j = 0; j < newItems.length; j++) {
        newIds.push(newItems[j].id);
      }
      sessionStorage.setItem('checkout_items', JSON.stringify(newIds));
    }
  }

  async function handleCheckout() {
    if (!shippingAddress.name || !shippingAddress.street || !shippingAddress.city || 
        !shippingAddress.state || !shippingAddress.zip) {
      toast.error('Please fill in all shipping fields');
      return;
    }

    setLoading(true);
    try {
      var payload = buildCheckoutPayload(items, {
        name: shippingAddress.name,
        street: shippingAddress.street,
        city: shippingAddress.city,
        state: shippingAddress.state,
        zip: shippingAddress.zip,
        country: 'USA'
      });

      var response = await api().post('/orders/create-checkout', payload);

      for (var j = 0; j < items.length; j++) {
        removeItemFromCart(items[j].id);
      }
      window.dispatchEvent(new Event('cartUpdated'));
      sessionStorage.removeItem('checkout_items');

      window.location.href = response.data.url;
    } catch (error) {
      console.error('Checkout error:', error);
      toast.error(error.response?.data?.detail || 'Checkout failed');
      setLoading(false);
    }
  }

  if (items.length === 0) {
    return (
      <div className="min-h-screen bg-[#0D0D0F]">
        <Navbar />
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <ShoppingCart className="w-16 h-16 mx-auto text-zinc-600 mb-4" />
          <h2 className="text-xl text-white mb-2">No items to checkout</h2>
          <Button onClick={function() { navigate('/marketplace'); }} className="btn-primary mt-4">
            Browse Marketplace
          </Button>
        </div>
      </div>
    );
  }

  // Build item elements without using .map()
  var itemElements = [];
  for (var k = 0; k < items.length; k++) {
    var item = items[k];
    itemElements.push(
      <CheckoutItem
        key={item.id}
        image={item.image_url}
        name={item.player_name}
        year={item.year}
        set={item.set_name}
        grade={item.grade_company + ' ' + item.grade_value}
        price={item.price.toFixed(2)}
        onRemove={function(id) { return function() { handleRemoveItem(id); }; }(item.id)}
      />
    );
  }

  // Build shipping lines without using .map()
  var shippingLines = [];
  if (totals && totals.sellerShipping) {
    for (var m = 0; m < totals.sellerShipping.length; m++) {
      var ss = totals.sellerShipping[m];
      shippingLines.push(
        <ShippingLine
          key={ss.seller_id}
          seller={ss.seller_name}
          amount={ss.shipping.toFixed(2)}
        />
      );
    }
  }

  var sellerName = items[0].seller_name || 'Seller';

  return (
    <div className="min-h-screen bg-[#0D0D0F]">
      <Navbar />
      
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button 
          onClick={function() { navigate(-1); }}
          className="flex items-center gap-2 text-zinc-400 hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4" />
          Back
        </button>

        <h1 className="text-3xl font-heading font-bold text-white mb-2">Checkout</h1>
        <p className="text-zinc-400 mb-8">
          {items.length} item{items.length > 1 ? 's' : ''} from {sellerName}
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          <div className="space-y-4">
            <h3 className="font-semibold text-white mb-3">Order Items</h3>
            {itemElements}

            <div className="stat-card p-4 space-y-3 mt-6">
              <h3 className="font-semibold text-white mb-3">Order Summary</h3>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Subtotal ({totals.itemCount} items)</span>
                <span className="text-white">${totals.subtotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Processing Fee (3.5%)</span>
                <span className="text-white">${totals.processingFee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-zinc-400">Tax (8%)</span>
                <span className="text-white">${totals.tax.toFixed(2)}</span>
              </div>
              
              {shippingLines}
              
              {totals.itemCount > 1 && totals.sellerCount === 1 && (
                <p className="text-xs text-emerald-400">
                  You save ${((5.99 * totals.itemCount) - totals.totalShipping).toFixed(2)} on shipping!
                </p>
              )}
              
              {totals.sellerCount > 1 && (
                <p className="text-xs text-amber-400">
                  {totals.sellerCount} sellers = {totals.sellerCount} separate shipments
                </p>
              )}
              
              <div className="border-t border-white/10 pt-3 flex justify-between">
                <span className="font-medium text-white">Total</span>
                <span className="font-mono font-bold text-emerald-400 text-lg">${totals.grandTotal.toFixed(2)}</span>
              </div>
            </div>
          </div>

          <div className="space-y-6">
            <div className="stat-card p-6">
              <h3 className="font-semibold text-white mb-4 flex items-center gap-2">
                <MapPin className="w-4 h-4" />
                Shipping Address
              </h3>
              
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400 text-sm">Full Name</Label>
                  <Input
                    value={shippingAddress.name}
                    onChange={function(e) { setShippingAddress(function(prev) { return { ...prev, name: e.target.value }; }); }}
                    className="input-field mt-1"
                    placeholder="John Doe"
                  />
                </div>
                <div>
                  <Label className="text-zinc-400 text-sm">Street Address</Label>
                  <Input
                    value={shippingAddress.street}
                    onChange={function(e) { setShippingAddress(function(prev) { return { ...prev, street: e.target.value }; }); }}
                    className="input-field mt-1"
                    placeholder="123 Main St"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-sm">City</Label>
                    <Input
                      value={shippingAddress.city}
                      onChange={function(e) { setShippingAddress(function(prev) { return { ...prev, city: e.target.value }; }); }}
                      className="input-field mt-1"
                      placeholder="New York"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">State</Label>
                    <Input
                      value={shippingAddress.state}
                      onChange={function(e) { setShippingAddress(function(prev) { return { ...prev, state: e.target.value }; }); }}
                      className="input-field mt-1"
                      placeholder="NY"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-zinc-400 text-sm">ZIP Code</Label>
                    <Input
                      value={shippingAddress.zip}
                      onChange={function(e) { setShippingAddress(function(prev) { return { ...prev, zip: e.target.value }; }); }}
                      className="input-field mt-1"
                      placeholder="10001"
                    />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-sm">Country</Label>
                    <Input
                      value={shippingAddress.country}
                      className="input-field mt-1"
                      disabled
                    />
                  </div>
                </div>
              </div>
            </div>

            <Button
              onClick={handleCheckout}
              className="w-full btn-primary h-14 text-lg"
              disabled={loading}
            >
              {loading ? <LoadingSpinner size="sm" /> : 'Pay $' + totals.grandTotal.toFixed(2)}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CheckoutPage;
